import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, MapPin, Phone, CheckCircle2, Wrench, Search } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { PageCTA } from "@/components/PageCTA";
import { getArticleBySlug } from "@/lib/learningHub";
import { getCityHubRoute } from "@/lib/cityHubRoutes";
import { ChooseYourCityService } from "@/components/ChooseYourCityService";
import { CITY_PAGES, type CityKey } from "@/lib/citiesData";
import { geoImageObject } from "@/lib/geoImageSchema";
import { buildLocalBusinessSchema, SCHEMA_LOCALBUSINESS_ID } from "@/lib/entitySchema";

const PHONE_DISPLAY = "(719) 419-3935";
const PHONE = "+1-719-419-3935";

export type CityLandingHero = {
  lead: string;
  gold: string;
  trail: string;
};

export type CityLandingHighlight = {
  title: string;
  body: string;
};

export type CityLandingService = {
  to: string;
  title: string;
  body: string;
};

export type CityLandingConfig = {
  slug: string; // e.g. "denver"
  name: string; // "Denver"
  region: string; // "Front Range"
  heroEyebrow: string;
  heroTitle: CityLandingHero;
  heroDescription: string;
  intro: string;
  neighborhoods: string[];
  marketSnapshot: string;
  highlights: CityLandingHighlight[];
  services: CityLandingService[];
  /** Slugs from learningHub ARTICLES, ordered by editorial priority for this city. */
  learnArticleSlugs: string[];
  /** Short editorial sentence introducing the Learn section for this city. */
  learnIntro: string;
  closingCta: string;
  /** Optional geo-tagged hero photo rendered below the hero. Also feeds og:image + ImageObject schema. */
  heroImage?: { src: string; alt: string; geo?: { lat: number; lng: number; city: string } };
};

export function CityLandingPage({ config }: { config: CityLandingConfig }) {
  const articles = config.learnArticleSlugs
    .map((s) => getArticleBySlug(s))
    .filter((a): a is NonNullable<ReturnType<typeof getArticleBySlug>> => Boolean(a));
  const hubHref = getCityHubRoute(config.slug);

  return (
    <SiteLayout>
      <PageHero
        eyebrow={config.heroEyebrow}
        title={
          <>
            {config.heroTitle.lead}
            <span className="text-brand-gold">{config.heroTitle.gold}</span>
            {config.heroTitle.trail}
          </>
        }
        description={config.heroDescription}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Locations", to: "/service-areas" },
          { label: config.name },
        ]}
      >
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-6 py-3 text-sm font-semibold text-brand-black shadow-lg transition-smooth hover:bg-brand-gold/90"
          >
            Get a {config.name} proposal <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 bg-brand-cream/5 px-6 py-3 text-sm font-semibold text-brand-cream transition-smooth hover:border-brand-gold/60 hover:text-brand-gold"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
        </div>
      </PageHero>

      {config.heroImage && (
        <section className="bg-brand-black pt-10 lg:pt-14">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <img
              src={config.heroImage.src}
              alt={config.heroImage.alt}
              loading="eager"
              fetchPriority="high"
              width={1536}
              height={864}
              data-geo-lat={config.heroImage.geo ? String(config.heroImage.geo.lat) : undefined}
              data-geo-lng={config.heroImage.geo ? String(config.heroImage.geo.lng) : undefined}
              data-geo-city={config.heroImage.geo?.city}
              className="aspect-[16/9] w-full rounded-2xl object-cover shadow-card"
            />
          </div>
        </section>
      )}

      <PricingTeaserStrip />


      {/* Intro + neighborhoods */}
      <section className="bg-brand-black py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <p className="text-lg leading-relaxed text-brand-cream/85">{config.intro}</p>
          {hubHref && (
            <a
              href={hubHref}
              className="mt-6 inline-flex items-center gap-2 rounded-xl border border-brand-gold/40 bg-brand-gold/10 px-5 py-3 text-sm font-bold text-brand-gold transition-smooth hover:bg-brand-gold/15"
            >
              See full {config.name} digital marketing services
              <ArrowRight className="h-4 w-4" />
            </a>
          )}
          <div className="mt-8 flex flex-wrap gap-2">
            {config.neighborhoods.map((n) => (
              <span
                key={n}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-gold/30 bg-brand-gold/5 px-3 py-1 text-xs font-medium text-brand-cream/80"
              >
                <MapPin className="h-3 w-3 text-brand-gold" /> {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Choose your city service — best-converting destination per service */}
      <ChooseYourCityService city={config.name} citySlug={config.slug as CityKey} />

      {/* Why this market */}
      <section className="border-y border-brand-cream/10 bg-gradient-dark py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Why {config.name} buyers convert differently
          </h2>
          <p className="mt-4 max-w-3xl text-brand-cream/85">{config.marketSnapshot}</p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {config.highlights.map((h) => (
              <div
                key={h.title}
                className="rounded-2xl border border-brand-cream/10 bg-brand-black/40 p-6 transition-smooth hover:border-brand-gold/40"
              >
                <CheckCircle2 className="h-6 w-6 text-brand-gold" />
                <h3 className="mt-3 text-lg font-semibold text-white">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-cream/85">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services in this city */}
      <section className="bg-brand-black py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <Wrench className="h-6 w-6 text-brand-gold" />
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Services we ship in {config.name}
            </h2>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {config.services.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group flex items-start gap-4 rounded-2xl border border-brand-cream/10 bg-brand-cream/5 p-6 transition-smooth hover:border-brand-gold/50 hover:bg-brand-gold/5"
              >
                <Search className="mt-1 h-5 w-5 flex-shrink-0 text-brand-gold" />
                <div>
                  <h3 className="text-lg font-semibold text-white group-hover:text-brand-gold">
                    {s.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-brand-cream/85">{s.body}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-gold">
                    Learn more <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Learn articles cross-link */}
      <section className="border-t border-brand-cream/10 bg-gradient-dark py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <BookOpen className="h-6 w-6 text-brand-gold" />
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              {config.name} owner reading list
            </h2>
          </div>
          <p className="mt-4 max-w-3xl text-brand-cream/85">{config.learnIntro}</p>

          <ul className="mt-10 grid gap-4 md:grid-cols-2">
            {articles.map((a) => {
              const Icon = a.icon;
              return (
                <li key={a.slug}>
                  <Link
                    to={a.slug}
                    className="group flex h-full items-start gap-4 rounded-2xl border border-brand-cream/10 bg-brand-black/40 p-5 transition-smooth hover:border-brand-gold/50 hover:bg-brand-gold/5"
                  >
                    <span className="mt-1 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-brand-gold/80">
                        {a.category} · {a.read}
                      </p>
                      <h3 className="mt-1 text-base font-semibold text-white group-hover:text-brand-gold">
                        {a.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-brand-cream/85">{a.desc}</p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="mt-8">
            <Link
              to="/learn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-gold hover:underline"
            >
              Browse the full Learn library <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <PageCTA
        eyebrow={`${config.name} marketing`}
        headline={`Ready to grow in ${config.name}?`}
        description={config.closingCta}
        seeOurWork={{ to: "/learn", label: `Read the ${config.name} owner reading list` }}
      />
    </SiteLayout>
  );
}

/** Build head() metadata for a city landing hub (e.g. /denver, /pueblo). */
export function buildCityLandingHead(
  config: CityLandingConfig,
  opts: {
    metaTitle: string;
    metaDescription: string;
    faqs?: { q: string; a: string }[];
  },
) {
  const canonical = `https://coloradowebimpressions.com/${config.slug}`;
  const cityData = CITY_PAGES.find((c) => c.slug === config.slug);
  const heroImageUrl = config.heroImage
    ? (config.heroImage.src.startsWith("http") ? config.heroImage.src : `https://coloradowebimpressions.com${config.heroImage.src}`)
    : undefined;
  const localBusiness = buildLocalBusinessSchema([config.name]);
  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      localBusiness,
      {
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: `Digital Marketing in ${config.name}`,
        description: opts.metaDescription,
        url: canonical,
        serviceType: "Digital Marketing",
        provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
        areaServed: {
          "@type": "City",
          name: config.name,
          containedInPlace: { "@type": "State", name: "Colorado" },
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://coloradowebimpressions.com/" },
          { "@type": "ListItem", position: 2, name: "Locations", item: "https://coloradowebimpressions.com/service-areas" },
          { "@type": "ListItem", position: 3, name: config.name, item: canonical },
        ],
      },
      // Geo-tagged ImageObject for the share/hero image — strengthens local
      // attribution in image search and AI grounding.
      geoImageObject({
        url: heroImageUrl ?? "https://coloradowebimpressions.com/og-default.jpg",
        caption: `Digital Marketing & SEO in ${config.name}, CO${(cityData?.zips ?? []).slice(0, 2).length ? ` (${(cityData?.zips ?? []).slice(0, 2).join(", ")})` : ""} — Colorado Web Impressions`,
        description: `${config.name}, Colorado digital marketing${cityData?.zips?.length ? ` serving ZIPs ${cityData.zips.slice(0, 4).join(", ")}` : ""}. ${opts.metaDescription}`,
        cityName: config.name,
        zips: cityData?.zips ?? [],
        geo: cityData ? { lat: cityData.geo.lat, lng: cityData.geo.lng } : undefined,
        width: 1200,
        height: 630,
      }),
      ...(opts.faqs && opts.faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "@id": `${canonical}#faq`,
              mainEntity: opts.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
    ],
  };
  return {
    meta: [
      { title: opts.metaTitle },
      { name: "description", content: opts.metaDescription },
      { property: "og:title", content: opts.metaTitle },
      { property: "og:description", content: opts.metaDescription },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: opts.metaTitle },
      { name: "twitter:description", content: opts.metaDescription },
      ...(heroImageUrl
        ? [
            { property: "og:image", content: heroImageUrl },
            { name: "twitter:image", content: heroImageUrl },
          ]
        : []),
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(graph),
      },
    ],
  };
}
